class RDVaradaHeroSlider {
  constructor(element) {
    this.element = element;

    this.slides = Array.from(
      element.querySelectorAll('[data-rd-slide]')
    );

    this.progressItems = Array.from(
      element.querySelectorAll('[data-rd-trigger]')
    );

    this.prevButton = element.querySelector(
      '[data-rd-prev]'
    );

    this.nextButton = element.querySelector(
      '[data-rd-next]'
    );

    this.prefersReducedMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

    this.autoplay =
      element.dataset.autoplay === 'true' &&
      !this.prefersReducedMotion;

    this.delay = Math.max(
      1000,
      Number(element.dataset.delay || 5000)
    );

    this.currentIndex = Math.max(
      0,
      this.slides.findIndex((slide) =>
        slide.classList.contains('is-active')
      )
    );

    this.timer = null;

    this.touchStartX = 0;
    this.touchEndX = 0;

    this.isHovered = false;
    this.hasFocus = false;
    this.isVisible = true;

    this.destroyed = false;

    if (!this.slides.length) return;

    this.bindEvents();
    this.observeVisibility();

    this.updateUI(this.currentIndex, true);

    this.handleActiveImage();

    if (this.slides.length > 1) {
      this.startAutoplay();
    }
  }


  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener(
        'click',
        () => {
          this.goTo(
            this.currentIndex - 1,
            true
          );
        }
      );
    }


    if (this.nextButton) {
      this.nextButton.addEventListener(
        'click',
        () => {
          this.goTo(
            this.currentIndex + 1,
            true
          );
        }
      );
    }


    this.progressItems.forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const index = Number(
            button.dataset.index || 0
          );

          this.goTo(index, true);
        }
      );
    });


    this.element.addEventListener(
      'mouseenter',
      () => {
        this.isHovered = true;
        this.stopAutoplay();
      }
    );


    this.element.addEventListener(
      'mouseleave',
      () => {
        this.isHovered = false;
        this.startAutoplay();
      }
    );


    this.element.addEventListener(
      'focusin',
      () => {
        this.hasFocus = true;
        this.stopAutoplay();
      }
    );


    this.element.addEventListener(
      'focusout',
      () => {
        window.requestAnimationFrame(() => {
          this.hasFocus =
            this.element.contains(
              document.activeElement
            );

          if (!this.hasFocus) {
            this.startAutoplay();
          }
        });
      }
    );


    this.element.addEventListener(
      'touchstart',
      (event) => {
        if (!event.changedTouches.length) {
          return;
        }

        this.touchStartX =
          event.changedTouches[0].clientX;
      },
      {
        passive: true
      }
    );


    this.element.addEventListener(
      'touchend',
      (event) => {
        if (!event.changedTouches.length) {
          return;
        }

        this.touchEndX =
          event.changedTouches[0].clientX;

        this.handleSwipe();
      },
      {
        passive: true
      }
    );


    this.visibilityHandler = () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    };


    document.addEventListener(
      'visibilitychange',
      this.visibilityHandler
    );


    this.resizeHandler = () => {
      this.handleActiveImage();
    };


    window.addEventListener(
      'resize',
      this.resizeHandler,
      {
        passive: true
      }
    );


    this.blockSelectHandler = (event) => {
      const block = event.target.closest(
        '[data-rd-slide]'
      );

      if (
        !block ||
        !this.element.contains(block)
      ) {
        return;
      }

      const index = Number(
        block.dataset.index || 0
      );

      this.goTo(index, false);
      this.stopAutoplay();
    };


    document.addEventListener(
      'shopify:block:select',
      this.blockSelectHandler
    );


    this.blockDeselectHandler = () => {
      this.startAutoplay();
    };


    document.addEventListener(
      'shopify:block:deselect',
      this.blockDeselectHandler
    );
  }


  observeVisibility() {
    if (!('IntersectionObserver' in window)) {
      return;
    }

    this.observer =
      new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (!entry) return;

          this.isVisible =
            entry.isIntersecting &&
            entry.intersectionRatio > 0;

          if (this.isVisible) {
            this.startAutoplay();
          } else {
            this.stopAutoplay();
          }
        },
        {
          threshold: 0.05
        }
      );

    this.observer.observe(this.element);
  }


  handleSwipe() {
    const difference =
      this.touchEndX - this.touchStartX;

    if (Math.abs(difference) < 45) {
      return;
    }

    if (difference > 0) {
      this.goTo(
        this.currentIndex - 1,
        true
      );
    } else {
      this.goTo(
        this.currentIndex + 1,
        true
      );
    }

    this.touchStartX = 0;
    this.touchEndX = 0;
  }


  normalizeIndex(index) {
    const total = this.slides.length;

    if (!total) return 0;

    if (index < 0) {
      return total - 1;
    }

    if (index >= total) {
      return 0;
    }

    return index;
  }


  goTo(index, userInitiated = false) {
    if (this.destroyed) return;

    if (this.slides.length < 2) {
      return;
    }

    const targetIndex =
      this.normalizeIndex(index);

    if (
      targetIndex === this.currentIndex &&
      !userInitiated
    ) {
      return;
    }

    this.currentIndex = targetIndex;

    this.updateUI(targetIndex);

    this.handleActiveImage();

    if (userInitiated) {
      this.stopAutoplay();
    }

    this.startAutoplay();
  }


  updateUI(activeIndex, initial = false) {
    this.slides.forEach(
      (slide, index) => {
        const isActive =
          index === activeIndex;

        slide.classList.toggle(
          'is-active',
          isActive
        );

        slide.setAttribute(
          'aria-hidden',
          isActive ? 'false' : 'true'
        );

        const link =
          slide.querySelector('a');

        if (link) {
          link.tabIndex =
            isActive ? 0 : -1;
        }
      }
    );


    this.progressItems.forEach(
      (button, index) => {
        const fill =
          button.querySelector(
            '.rd-vs__progress-fill'
          );

        button.classList.remove(
          'is-active',
          'is-complete'
        );

        button.setAttribute(
          'aria-selected',
          index === activeIndex
            ? 'true'
            : 'false'
        );

        button.tabIndex =
          index === activeIndex
            ? 0
            : -1;

        if (fill) {
          fill.classList.remove(
            'is-running'
          );

          fill.style.removeProperty(
            '--rd-progress-duration'
          );

          /*
           * Force animation reset.
           */
          void fill.offsetWidth;
        }


        if (index < activeIndex) {
          button.classList.add(
            'is-complete'
          );
        }


        if (index === activeIndex) {
          button.classList.add(
            'is-active'
          );
        }
      }
    );


    if (!initial) {
      this.element.dispatchEvent(
        new CustomEvent(
          'rd:slide:change',
          {
            detail: {
              index: activeIndex
            }
          }
        )
      );
    }
  }


  /*
   * The browser already calculates the active
   * slide height naturally because the active
   * slide remains position: relative.
   *
   * This method simply makes sure responsive
   * image changes and late-loading images are
   * reflected immediately.
   */
  handleActiveImage() {
    const activeSlide =
      this.slides[this.currentIndex];

    if (!activeSlide) return;

    const image =
      activeSlide.querySelector(
        '[data-rd-slide-image]'
      );

    if (!image) return;


    if (image.complete) {
      this.refreshLayout();
      return;
    }


    image.addEventListener(
      'load',
      () => {
        this.refreshLayout();
      },
      {
        once: true
      }
    );
  }


  refreshLayout() {
    /*
     * Reading offsetHeight forces the browser
     * to resolve the selected <picture> source.
     *
     * No explicit height is assigned.
     */
    const activeSlide =
      this.slides[this.currentIndex];

    if (!activeSlide) return;

    void activeSlide.offsetHeight;

    this.element.dispatchEvent(
      new CustomEvent(
        'rd:slider:resize'
      )
    );
  }


  runProgress() {
    if (
      !this.autoplay ||
      this.destroyed
    ) {
      return;
    }

    const activeItem =
      this.progressItems[
        this.currentIndex
      ];

    if (!activeItem) return;

    const fill =
      activeItem.querySelector(
        '.rd-vs__progress-fill'
      );

    if (!fill) return;

    fill.classList.remove(
      'is-running'
    );

    fill.style.setProperty(
      '--rd-progress-duration',
      `${this.delay}ms`
    );

    void fill.offsetWidth;

    fill.classList.add(
      'is-running'
    );
  }


  canAutoplay() {
    return (
      this.autoplay &&
      this.slides.length > 1 &&
      !this.destroyed &&
      !document.hidden &&
      !this.isHovered &&
      !this.hasFocus &&
      this.isVisible
    );
  }


  startAutoplay() {
    if (!this.canAutoplay()) {
      return;
    }

    this.stopTimerOnly();

    this.resetActiveProgress();
    this.runProgress();

    this.timer =
      window.setTimeout(
        () => {
          this.timer = null;

          this.goTo(
            this.currentIndex + 1,
            false
          );
        },
        this.delay
      );
  }


  stopTimerOnly() {
    if (!this.timer) {
      return;
    }

    window.clearTimeout(
      this.timer
    );

    this.timer = null;
  }


  resetActiveProgress() {
    const activeItem =
      this.progressItems[
        this.currentIndex
      ];

    if (!activeItem) return;

    const fill =
      activeItem.querySelector(
        '.rd-vs__progress-fill'
      );

    if (!fill) return;

    fill.classList.remove(
      'is-running'
    );

    fill.style.removeProperty(
      '--rd-progress-duration'
    );

    void fill.offsetWidth;
  }


  stopAutoplay() {
    this.stopTimerOnly();
    this.resetActiveProgress();
  }


  destroy() {
    this.destroyed = true;

    this.stopAutoplay();

    if (this.observer) {
      this.observer.disconnect();
    }

    document.removeEventListener(
      'visibilitychange',
      this.visibilityHandler
    );

    window.removeEventListener(
      'resize',
      this.resizeHandler
    );

    document.removeEventListener(
      'shopify:block:select',
      this.blockSelectHandler
    );

    document.removeEventListener(
      'shopify:block:deselect',
      this.blockDeselectHandler
    );
  }
}


function initRDVaradaSliders(
  root = document
) {
  const sliders =
    root.matches?.('[data-rd-slider]')
      ? [root]
      : Array.from(
          root.querySelectorAll(
            '[data-rd-slider]'
          )
        );

  sliders.forEach((slider) => {
    if (slider.__rdSlider) {
      return;
    }

    slider.__rdSlider =
      new RDVaradaHeroSlider(
        slider
      );
  });
}


function destroyRDVaradaSliders(
  root
) {
  const sliders =
    root.matches?.('[data-rd-slider]')
      ? [root]
      : Array.from(
          root.querySelectorAll(
            '[data-rd-slider]'
          )
        );

  sliders.forEach((slider) => {
    if (!slider.__rdSlider) {
      return;
    }

    slider.__rdSlider.destroy();
    slider.__rdSlider = null;
  });
}


if (
  document.readyState === 'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initRDVaradaSliders(
        document
      );
    }
  );
} else {
  initRDVaradaSliders(
    document
  );
}


document.addEventListener(
  'shopify:section:load',
  (event) => {
    initRDVaradaSliders(
      event.target
    );
  }
);


document.addEventListener(
  'shopify:section:unload',
  (event) => {
    destroyRDVaradaSliders(
      event.target
    );
  }
);